import { Box, Flex, Text } from '@indeed/ifl-components';
import { expressive } from '@indeed/ifl-primitives';
import Heading from '../heading';
import { Localization, SalaryComponentProps } from '../../types';
import { i18nNumberFormat } from '../../utils/localizationUtils';

const AvgSalary: React.FC<SalaryComponentProps> = ({
  label,
  unit,
  minRate,
  maxRate,
  avgRate,
  info,
  currencyCode,
  localization,
}) => {
  const getAvgPercent = (min: number, max: number, avg: number): string => {
    // Find the percentage of the average salary compared to the range between min and max values
    const percent = ((avg - min) * 100) / (max - min);
    return `${percent.toFixed(2)}%`;
  };
  unit = localizePayPeriodString(unit, localization);
  const decimalPlaces = unit === 'hour' ? 2 : 0;

  return (
    <Box sx={{ pb: 4 }}>
      <Heading
        text={label}
        tooltipText={info}
        tooltipLabel={`More information about ${label}`}
      />
      <Flex sx={{ gap: 6, alignItems: 'center', flexFlow: 'row wrap' }}>
        <Text
          level={6}
          sx={{ fontWeight: 'bold', mr: 1, color: 'foreground.strong' }}
        >
          {i18nNumberFormat(
            avgRate,
            localization.locale,
            'currency',
            decimalPlaces,
            currencyCode
          )}
          <Text level={3}> / {unit}</Text>
        </Text>
        <Flex
          sx={{
            alignItems: 'center',
            flex: '1 0 65%',
            justifyContent: 'space-between',
            mt: 4,
            position: 'relative',
            zIndex: '1',
            // Solid line
            '::after': {
              backgroundColor: expressive.purple[300],
              bottom: '50%',
              content: '""',
              display: 'block',
              height: '8px',
              left: '0',
              position: 'absolute',
              transform: 'translateY(50%)',
              width: '100%',
            },
            // Dot indicator
            '::before': {
              backgroundColor: expressive.purple[900],
              bottom: '50%',
              content: '""',
              display: 'block',
              height: '8px',
              left: getAvgPercent(minRate, maxRate, avgRate),
              position: 'absolute',
              transform: 'translate(-50%, 50%)',
              width: '8px',
              zIndex: '2',
            },
          }}
        >
          <Text sx={{ position: 'relative', top: 4 }} level={2}>
            <Text sx={{ color: expressive.purple[700], fontWeight: 'bold' }}>
              {localization.low}{' '}
            </Text>
            {i18nNumberFormat(
              minRate,
              localization.locale,
              'currency',
              decimalPlaces,
              currencyCode
            )}
          </Text>
          <Text
            level={2}
            sx={{
              top: -4,
              left: getAvgPercent(minRate, maxRate, avgRate),
              position: 'absolute',
              transform: 'translateX(-50%)',
              zIndex: '2',
            }}
          >
            <Text
              sx={{
                color: expressive.purple[700],
                fontWeight: 'bold',
              }}
            >
              {localization.average}{' '}
            </Text>
            {i18nNumberFormat(
              avgRate,
              localization.locale,
              'currency',
              decimalPlaces,
              currencyCode
            )}
          </Text>

          <Text sx={{ position: 'relative', top: 4 }} level={2}>
            <Text sx={{ color: expressive.purple[700], fontWeight: 'bold' }}>
              {localization.high}{' '}
            </Text>
            {i18nNumberFormat(
              maxRate,
              localization.locale,
              'currency',
              unit === 'hour' ? 2 : 0,
              currencyCode
            )}
          </Text>
        </Flex>
      </Flex>
    </Box>
  );
};

function localizePayPeriodString(
  payPeriod: string,
  localization: Localization
) {
  switch (payPeriod) {
    case 'YEARLY':
      return localization.year;
    case 'MONTHLY':
      return localization.month;
    case 'WEEKLY':
      return localization.week;
    case 'DAILY':
      return localization.day;
    case 'HOURLY':
      return localization.hour;
    default:
      return payPeriod;
  }
}

export default AvgSalary;
